const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MANUAL_STOCK_MOVEMENT_TYPES = new Set(["in", "out", "adjust"]);

const ok = () => ({ ok: true, message: "", fieldErrors: {} });

const result = (fieldErrors) => {
  const first = Object.values(fieldErrors)[0];
  return first ? { ok: false, message: first, fieldErrors } : ok();
};

const required = (v) => String(v ?? "").trim().length > 0;

const toNumber = (v) => Number(v);

const isNonNegative = (v) => Number.isFinite(toNumber(v)) && toNumber(v) >= 0;

const isPositive = (v) => Number.isFinite(toNumber(v)) && toNumber(v) > 0;

const isValidDate = (v) => {
  if (!required(v)) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};

const pctValid = (v) => Number.isFinite(toNumber(v)) && toNumber(v) >= 0 && toNumber(v) <= 100;

export function validateEmployee(payload) {
  const errors = {};
  if (!required(payload?.code)) errors.code = "Mã nhân viên là bắt buộc";
  if (!required(payload?.full_name)) errors.full_name = "Họ tên là bắt buộc";
  if (!isNonNegative(payload?.base_salary ?? 0)) errors.base_salary = "Lương cơ bản phải >= 0";
  if (required(payload?.email) && !EMAIL_RE.test(String(payload.email).trim())) errors.email = "Email không hợp lệ";
  if (required(payload?.hire_date) && !isValidDate(payload.hire_date)) errors.hire_date = "Ngày vào không hợp lệ";
  return result(errors);
}

export function validatePayrollPeriod(payload) {
  const errors = {};
  if (!required(payload?.code)) errors.code = "Mã kỳ lương là bắt buộc";
  if (!required(payload?.from_date)) errors.from_date = "Từ ngày là bắt buộc";
  else if (!isValidDate(payload.from_date)) errors.from_date = "Từ ngày không hợp lệ";
  if (!required(payload?.to_date)) errors.to_date = "Đến ngày là bắt buộc";
  else if (!isValidDate(payload.to_date)) errors.to_date = "Đến ngày không hợp lệ";
  if (isValidDate(payload?.from_date) && isValidDate(payload?.to_date) && payload.from_date > payload.to_date) {
    errors.to_date = "Đến ngày phải sau hoặc bằng từ ngày";
  }
  if (!isNonNegative(payload?.bonus_pool_total ?? 0)) errors.bonus_pool_total = "Quỹ thưởng phải >= 0";

  const pctFields = ["bonus_a_pct", "bonus_b_pct", "bonus_c_pct"];
  pctFields.forEach((field) => {
    if (!pctValid(payload?.[field] ?? 0)) errors[field] = "Tỷ lệ thưởng phải trong khoảng 0..100";
  });
  const totalPct = pctFields.reduce((sum, field) => sum + toNumber(payload?.[field] ?? 0), 0);
  if (pctFields.every((field) => pctValid(payload?.[field] ?? 0)) && totalPct > 100) {
    errors.bonus_total_pct = "Tổng tỷ lệ A/B/C không được vượt 100%";
  }
  return result(errors);
}

export function validateStockMovement(payload) {
  const errors = {};
  if (!required(payload?.sku_id)) errors.sku_id = "SKU là bắt buộc";
  if (!required(payload?.branch_id)) errors.branch_id = "Chi nhánh là bắt buộc";
  if (payload?.movement_type === "adjust") {
    if (!Number.isFinite(toNumber(payload?.qty)) || toNumber(payload.qty) === 0) errors.qty = "Số lượng điều chỉnh phải khác 0";
  } else if (!isPositive(payload?.qty)) errors.qty = "Số lượng phải > 0";
  if (!isNonNegative(payload?.unit_cost ?? 0)) errors.unit_cost = "Đơn giá vốn phải >= 0";
  if (!MANUAL_STOCK_MOVEMENT_TYPES.has(payload?.movement_type)) errors.movement_type = "Điều chuyển kho phải dùng API điều chuyển";
  return result(errors);
}

const POS_FIELD_LABELS = {
  branch_id: "Chi nhánh",
  table_no: "Số bàn",
  customer_id: "Khách hàng",
  lines: "Giỏ hàng",
};

export function validatePosCheckout(payload) {
  const errors = {};
  const lines = Array.isArray(payload?.lines) ? payload.lines : [];
  if (!lines.length) errors.lines = "Giỏ hàng trống";
  if (!required(payload?.branch_id)) errors.branch_id = "Chi nhánh là bắt buộc";
  lines.forEach((line, index) => {
    if (!isPositive(line?.qty)) errors[`lines.${index}.qty`] = "Số lượng phải > 0";
    if (!isNonNegative(line?.unit_price)) errors[`lines.${index}.unit_price`] = "Đơn giá phải >= 0";
  });
  if (payload?.channel === "restaurant" && !required(payload?.table_no)) errors.table_no = "Số bàn là bắt buộc";
  if (payload?.channel === "b2b" && !required(payload?.customer_id)) errors.customer_id = "Khách hàng B2B là bắt buộc";

  const missingFields = Object.keys(errors)
    .filter((key) => POS_FIELD_LABELS[key])
    .map((key) => POS_FIELD_LABELS[key]);
  if (missingFields.length) {
    const summary = `Thiếu: ${missingFields.join(", ")}`;
    return { ok: false, message: summary, fieldErrors: errors };
  }
  return result(errors);
}
